import React, {FC, memo} from 'react';

import {UnreadStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {
  computeCommentDraftHasDisplayableContent,
  computeIsDraftComment,
} from '../../../../../helpers/models/draftCommentHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {ConversationHighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {DraftSummaryModel} from '../../../../../models/api/draftSummaryModel';
import {MessageModel, MessageStatusesEnum} from '../../../../../models/api/messageModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {
  BumpCommentModel,
  CommentDraftSummary,
  LastCommentDraftModel,
  LastCommentModel,
} from '../../../../../store/data/conversations/conversationCellSelectors';
import {CellComment, renderSummary} from './cellComment';
import {CellCommentDraftSummary} from './cellCommentDraftSummary';
import {CellCommentingPrecog} from './cellCommentingPrecog';
import {CellCommentLine, CellCommentLineProps} from './cellCommentLine';
import {CellConversationShared} from './cellConversationShared';
import {CellDraft} from './cellDraft';
import {CellDraftSeeds} from './cellDraftSeeds';
import {CellReplyingPrecog} from './cellReplyingPrecog';
import {CellRuleNotify} from './cellRuleNotify';
import {
  CellSearchActivitySummaryHighlight,
  VisualSearchActivityModel,
} from './cellSearchActivitySummaryHighlight';
import {CellSummary} from './cellSummary';

/*
 * Props.
 */

interface CellActivityProps {
  mentionableAliases?: ReadonlySet<string>;
  summary: string;
  commentingTeammate?: TeammateModel;
  replyingTeammate?: TeammateModel;
  draftSeedAuthors?: ReadonlyArray<TeammateModel>;
  lastComment: LastCommentModel | undefined;
  lastCommentDraft: LastCommentDraftModel | undefined;
  lastManualMessage: MessageModel | undefined;
  isSelected?: boolean;
  draft: DraftSummaryModel | undefined;
  commentDraftSummary: CommentDraftSummary | undefined;
  useEmptyDraft?: boolean;
  bumpActivity?: ActivityModel;
  bumpComment?: BumpCommentModel;
  bumpTeammateSource?: TeammateModel;
  unreadStatus?: UnreadStatusesEnum;
  userId?: number;
  hasListSendingMessagesInSentFolder?: boolean;
  hasAutoShareDrafts?: boolean;
  isInDraftsFolder?: boolean;

  // Search
  conversationHighlight?: ConversationHighlightModel;
  searchActivity?: VisualSearchActivityModel;
  searchResultCommentSummary?: VisualSearchResultCommentSummary;
  conversationId?: number;
}

export type VisualSearchResultCommentSummary = Pick<CellCommentLineProps, 'text' | 'author'>;

/*
 * Component.
 */

export const CellActivity: FC<CellActivityProps> = memo((props) => {
  const {
    isSelected,
    lastManualMessage,
    searchActivity,
    conversationHighlight,
    searchResultCommentSummary,
    mentionableAliases,
  } = props;

  // The replying precog always takes precedence.
  if (props.replyingTeammate) {
    return <CellReplyingPrecog author={props.replyingTeammate} isSelected={isSelected} />;
  }

  // After that comes the commenting precog.
  if (props.commentingTeammate) {
    return <CellCommentingPrecog author={props.commentingTeammate} isSelected={isSelected} />;
  }

  // If we are in search mode show the highlighted summary.
  if (conversationHighlight?.summary) {
    return (
      <CellSearchActivitySummaryHighlight
        isSelected={isSelected}
        summaryHighlight={conversationHighlight.summary}
        searchActivity={searchActivity}
        mentionableAliases={mentionableAliases}
      />
    );
  }

  // If we are in search mode show the attachment's filename.
  if (conversationHighlight?.attachments) {
    return (
      <CellSummary
        isSelected={isSelected}
        summary={conversationHighlight?.attachments.text}
        highlight={conversationHighlight.attachments}
        type={searchActivity?.visualType}
        attachmentContentType={searchActivity?.visualContentType}
      />
    );
  }

  // Draft replies are next.
  const isPendingDraft = props.draft && props.draft.status === MessageStatusesEnum.DRAFT_PENDING;
  const isFailedDraft = props.draft && props.draft.status === MessageStatusesEnum.DRAFT_FAILED;
  const isSendingDraft = props.draft && props.draft.status === MessageStatusesEnum.DRAFT_SENDING;
  const hasDraftContent = props.draft && (props.draft.blurb.trim() || props.draft.hasAttachments);
  const shouldDisplayDraft = props.hasListSendingMessagesInSentFolder
    ? !isSendingDraft && (isPendingDraft || isFailedDraft || hasDraftContent || props.useEmptyDraft)
    : isPendingDraft || isFailedDraft || hasDraftContent || props.useEmptyDraft;
  if (props.draft && shouldDisplayDraft) {
    return (
      <CellDraft
        isInDraftsFolder={props.isInDraftsFolder}
        hasAutoShareDrafts={props.hasAutoShareDrafts}
        draft={props.draft}
        isSelected={isSelected}
        conversationId={props.conversationId}
      />
    );
  }

  if (props.draftSeedAuthors && props.draftSeedAuthors.length) {
    return <CellDraftSeeds authors={props.draftSeedAuthors} isSelected={isSelected} />;
  }

  const {bumpActivity, bumpComment, bumpTeammateSource, commentDraftSummary, lastCommentDraft, unreadStatus} =
    props;

  // Draft comments are next.
  // If we have a comment with the draft status available, we use that. This draft comment would the most
  // information on it--i.e. list of attachments and annotations.
  // If we have a comment draft summary, we use that. This draft comment summary will have less
  // information--i.e. no attachments or annotations list, just a hasAttachments boolean.
  if (lastCommentDraft && computeShouldUseLastCommentDraft(lastCommentDraft, commentDraftSummary)) {
    return (
      <CellComment
        comment={lastCommentDraft.comment}
        author={lastCommentDraft.author}
        mentionableAliases={mentionableAliases}
        isSelected={isSelected}
      />
    );
  }

  if (commentDraftSummary && computeShouldUseCommentDraftSummary(lastCommentDraft, commentDraftSummary)) {
    return (
      <CellCommentDraftSummary
        summary={commentDraftSummary.summary}
        author={commentDraftSummary.author}
        mentionableAliases={mentionableAliases}
        isSelected={isSelected}
      />
    );
  }

  // If we have a bump comment, render it.
  if (bumpComment) {
    return (
      <CellComment
        comment={bumpComment.comment}
        author={bumpComment.author}
        hasFollowingComments={!bumpComment.isLastComment}
        mentionableAliases={mentionableAliases}
        isSelected={isSelected}
        isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
      />
    );
  }

  const {lastComment} = props;

  // If we have a basic bump activity, render it.
  if (bumpActivity && bumpActivity.visualType === ActivityVisualTypesEnum.BASIC) {
    // If we have a tracker_share, render it
    if (bumpActivity.type === BasicActivityTypesEnum.TRACKER_SHARE && bumpTeammateSource) {
      return <CellConversationShared source={bumpTeammateSource} isSelected={isSelected} />;
    }

    // If we have a notification from a rule, render it.
    if (bumpActivity.type === BasicActivityTypesEnum.RULE_NOTIFY) {
      return <CellRuleNotify isSelected={isSelected} />;
    }
  }

  // Render the last comment if present and if one of the following conditions are met:
  //  - there's no last manual message
  //  - the last manual message is older than the last comment.
  if (lastComment && (!lastManualMessage || lastManualMessage.sentAt < lastComment.comment.sentAt)) {
    return (
      <CellComment
        {...lastComment}
        mentionableAliases={mentionableAliases}
        isSelected={isSelected}
        isUnread={unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD}
      />
    );
  }

  // Render the lastManualMessage if present.
  if (lastManualMessage) {
    const {isReply, blurb} = lastManualMessage;
    return <CellSummary isSelected={isSelected} isReply={isReply} summary={blurb} />;
  }

  if (searchResultCommentSummary) {
    return (
      <CellCommentLine
        isSelected={isSelected}
        text={searchResultCommentSummary.text}
        author={searchResultCommentSummary.author}
        annotation={undefined}
        renderSummary={renderSummary}
        shouldDisplayAttachmentIcon={false}
        shouldUseEllipsisOverflow={false}
        mentionableAliases={mentionableAliases}
      />
    );
  }

  // Default to rendering the conversation summary.
  return <CellSummary isSelected={isSelected} summary={props.summary} />;
});

/*
 * Helpers.
 */

function computeIsCommentDraftNewerThanSummary(
  lastCommentDraft: LastCommentDraftModel,
  commentDraftSummary: CommentDraftSummary | undefined,
): boolean {
  return lastCommentDraft.comment.updatedAt >= (commentDraftSummary?.summary.updatedAt ?? 0);
}

function computeShouldUseLastCommentDraft(
  lastCommentDraft: LastCommentDraftModel | undefined,
  commentDraftSummary: CommentDraftSummary | undefined,
): boolean {
  return (
    lastCommentDraft !== undefined &&
    computeIsDraftComment(lastCommentDraft.comment) &&
    computeCommentDraftHasDisplayableContent(lastCommentDraft.comment) &&
    computeIsCommentDraftNewerThanSummary(lastCommentDraft, commentDraftSummary)
  );
}

function computeShouldUseCommentDraftSummary(
  lastCommentDraft: LastCommentDraftModel | undefined,
  commentDraftSummary: CommentDraftSummary | undefined,
): boolean {
  // Because an actual comment is always more informative than a summary, we won't use the commentDraftSummary
  // if a lastCommentDraft exists, even if it does not have enough data for it to be displayable
  // (e.g. the comment draft has no text, attachments).
  return lastCommentDraft === undefined && commentDraftSummary !== undefined;
}
